import React from 'react';
import { useTranslation } from 'react-i18next';
import { Smile, Frown, Meh } from 'lucide-react';

interface SentimentBadgeProps {
  sentiment: 'positive' | 'negative' | 'neutral';
  score?: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const SentimentBadge: React.FC<SentimentBadgeProps> = ({
  sentiment,
  score,
  size = 'md',
  showLabel = true
}) => {
  const { t } = useTranslation();
  const config = {
    positive: {
      icon: Smile,
      label: t('listening.positive', 'Positive'),
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      textColor: 'text-green-700 dark:text-green-400',
      iconColor: 'text-green-500'
    },
    negative: {
      icon: Frown,
      label: t('listening.negative', 'Negative'),
      bgColor: 'bg-red-100 dark:bg-red-900/30',
      textColor: 'text-red-700 dark:text-red-400',
      iconColor: 'text-red-500'
    },
    neutral: {
      icon: Meh,
      label: t('listening.neutral', 'Neutral'),
      bgColor: 'bg-gray-100 dark:bg-gray-700',
      textColor: 'text-gray-700 dark:text-gray-400',
      iconColor: 'text-gray-500'
    }
  };

  const sizeConfig = {
    sm: {
      wrapper: 'px-1.5 py-0.5 text-xs gap-1',
      icon: 'w-3 h-3'
    },
    md: {
      wrapper: 'px-2 py-1 text-sm gap-1.5',
      icon: 'w-4 h-4'
    },
    lg: {
      wrapper: 'px-3 py-1.5 text-base gap-2',
      icon: 'w-5 h-5'
    }
  };

  const { icon: Icon, label, bgColor, textColor, iconColor } = config[sentiment];
  const { wrapper, icon } = sizeConfig[size];

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${bgColor} ${textColor} ${wrapper}`}
    >
      <Icon className={`${icon} ${iconColor}`} />
      {showLabel && <span>{label}</span>}
      {score !== undefined && (
        <span className="opacity-75">({Math.round(score * 100)}%)</span>
      )}
    </span>
  );
};

export default SentimentBadge;
